// Admin "User Event Feedback" tab. Lists every post-event feedback
// row collected from the mobile modal. Filterable by attended /
// no-show and source.
//
// Server endpoint: GET /admin/event-feedback (zona-superadmin-server
// PR #120). Returns paginated rows with user + event + venue hydrated.

import "./EventFeedback.scss";

import { useEffect, useMemo, useState } from "react";
import { format } from "date-fns";

import SideBar from "../../components/SideBar/SideBar";
import TopNav from "../../components/TopNav/TopNav";
import TableColumn from "../../components/TableColumn/TableColumn";
import api from "../../services/api";

const ATTENDED_FILTERS = [
  { key: "", label: "All" },
  { key: "true", label: "Attended" },
  { key: "false", label: "No-show" },
];

const SOURCE_FILTERS = [
  { key: "", label: "All sources" },
  { key: "ticket", label: "Tickets" },
  { key: "guestlist", label: "Guestlist" },
  { key: "bottle_service", label: "Bottle Service" },
];

const SOURCE_LABEL = {
  ticket: "Tickets",
  guestlist: "Guestlist",
  bottle_service: "Bottle Service",
};

const NO_SHOW_REASON_LABEL = {
  change_of_plans: "Change of plans",
  qr_not_working: "QR not working",
  denied_at_door: "Denied at door",
  other: "Other",
};

function EventFeedback() {
  const records = 25;

  const [loading, setLoading] = useState(true);
  const [rows, setRows] = useState([]);
  const [totalPages, setTotalPages] = useState(0);
  const [currentPage, setCurrentPage] = useState(1);

  const [attendedFilter, setAttendedFilter] = useState("");
  const [sourceFilter, setSourceFilter] = useState("");

  const columns = useMemo(
    () => ["User", "Event", "Source", "Attended", "Rating / Reason", "Comment", "When"],
    [],
  );

  useEffect(() => {
    let cancelled = false;
    (async () => {
      setLoading(true);
      try {
        const { data } = await api.get("/admin/event-feedback", {
          params: {
            page: currentPage,
            records,
            ...(attendedFilter ? { attended: attendedFilter } : {}),
            ...(sourceFilter ? { source: sourceFilter } : {}),
          },
        });
        if (cancelled) return;
        const list = data?.data || [];
        const formatted = list.map((row) => ({
          User: row.user?.name || "—",
          Email: row.user?.email || "",
          Event: row.event?.name || "—",
          Venue: row.event?.venue_name || "",
          Source: SOURCE_LABEL[row.source] || row.source || "—",
          Attended: row.attended
            ? <span className="event-feedback__pill event-feedback__pill--attended">Yes</span>
            : <span className="event-feedback__pill event-feedback__pill--no-show">No</span>,
          "Rating / Reason": row.attended
            ? <span className="event-feedback__rating">{renderStars(row.star_rating)}</span>
            : (NO_SHOW_REASON_LABEL[row.no_show_reason] || row.no_show_reason || "—"),
          Comment: renderCommentCell(row),
          When: row.created_at
            ? format(new Date(row.created_at), "MMM d, yyyy HH:mm")
            : "—",
          _id: row._id,
        }));
        setRows(formatted);
        const total = Number(data?.total) || formatted.length;
        setTotalPages(Math.max(1, Math.ceil(total / records)));
      } catch (err) {
        if (cancelled) return;
        console.error("EventFeedback fetch failed:", err);
        setRows([]);
        setTotalPages(0);
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [currentPage, attendedFilter, sourceFilter]);

  const handleAttendedChange = (key) => {
    setAttendedFilter(key);
    setCurrentPage(1);
  };
  const handleSourceChange = (key) => {
    setSourceFilter(key);
    setCurrentPage(1);
  };

  return (
    <main>
      <TopNav />
      <div className="main-container">
        <SideBar />
        <article className="page-content">
          <section className="event-feedback">
            <h1 style={{ fontSize: 28, fontWeight: 700, color: "#fff", margin: "0 0 20px 0" }}>
              User Event Feedback
            </h1>
            <p style={{ color: "rgba(255,255,255,0.55)", fontSize: 13, lineHeight: 1.5, margin: "-12px 0 20px 0", maxWidth: 720 }}>
              Feedback collected via the mobile post-event modal. Shown to a user 24h after an event they engaged with ends. Attended users rate the night, no-shows tell us why they couldn't make it.
            </p>

            <div className="event-feedback__filter-row">
              {ATTENDED_FILTERS.map((f) => (
                <button
                  key={f.key || "all-att"}
                  className={`event-feedback__chip${attendedFilter === f.key ? " is-active" : ""}`}
                  onClick={() => handleAttendedChange(f.key)}
                >{f.label}</button>
              ))}
            </div>
            <div className="event-feedback__filter-row">
              {SOURCE_FILTERS.map((f) => (
                <button
                  key={f.key || "all-src"}
                  className={`event-feedback__chip${sourceFilter === f.key ? " is-active" : ""}`}
                  onClick={() => handleSourceChange(f.key)}
                >{f.label}</button>
              ))}
            </div>

            <TableColumn
              title="Event Feedback"
              columns={columns}
              data={rows}
              loading={loading}
              hasSearch={false}
              hasExport={false}
              hasHeader={false}
              totalPages={totalPages}
              currentPage={currentPage}
              onPageChange={setCurrentPage}
            />
          </section>
        </article>
      </div>
    </main>
  );
}

function renderStars(rating) {
  const r = Number(rating) || 0;
  if (!r) return "—";
  return "★".repeat(r) + "☆".repeat(Math.max(0, 5 - r));
}

function renderCommentCell(row) {
  if (row.attended) {
    return row.comment
      ? <span style={{ color: "#fff" }}>{truncate(row.comment, 80)}</span>
      : <span style={{ color: "rgba(255,255,255,0.3)" }}>—</span>;
  }
  if (row.no_show_reason === "other" && row.no_show_other) {
    return <span style={{ color: "#fff" }}>{truncate(row.no_show_other, 80)}</span>;
  }
  return <span style={{ color: "rgba(255,255,255,0.3)" }}>—</span>;
}

function truncate(s, n) {
  if (!s) return "";
  return s.length > n ? s.slice(0, n) + "…" : s;
}

export default EventFeedback;
